import { callLambdaApi } from "../callLambdaApi";
import { ApiNames, Methods } from "../constants";

/**
 * DatasetApiCommunicator
 * 
 * Handles all API communication for dataset-related operations.
 * Provides methods for CRUD operations on datasets including processing,
 * model management, and file handling.
 * 
 * This communicator follows the same pattern as other API communicators
 * in the application and uses the centralized callLambdaApi function.
 */
export class DatasetApiCommunicator {
  /**
   * @param {object} requestBody
   * @param {string} requestBody.modelId
   * @param {string} requestBody.modelName
   * @param {?string} requestBody.uploadFileName - Needs to exist for item to return uploadUrls
   */
  create(
    requestBody = {
      datasetName: "Multi model dataset",
      datasetType: "training",
      modelIds: [],
    }
  ) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL(),
      payload: {
        body: {
          ...requestBody,
        },
      },
    });
  }

  /**
   * @param {string} id
   * @param {boolean} includeModels
   * @returns {Promise<import('api').GETResponse<import('models').Dataset>}
   */
  fetch(id, includeModels = false) {
    console.log("fetch()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}?includeModels=${includeModels}`,
    });
  }

  /**
   * @returns {Promise<import('api').GETResponse<import('models').Dataset[]>}
   */
  list() {
    console.log("list()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL(),
    });
  }

  update(requestBody) {
    if ("models" in requestBody) {
      console.log("found models in requestBody. Deleting");
      delete requestBody.models;
    }
    console.log("update()", requestBody);
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${requestBody.datasetId}`,
      payload: { body: requestBody },
    });
  }

  processDataset(datasetId, smoothness) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/process/${datasetId}`,
      payload: { body: { smoothness: smoothness } },
    });
  }

  reprocessDatasetModel(datasetId, submittedAt, modelId) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/process/${datasetId}/${submittedAt}/${modelId}`,
      payload: { body: {} },
    });
  }

  deleteProcessingResult(id, submittedAt) {
    return callLambdaApi({
      method: Methods.DELETE,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}/process/${submittedAt}`,
    });
  }

  delete(id) {
    return callLambdaApi({
      method: Methods.DELETE,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}`,
    });
  }

  /**
   * Returns the default serverless api endpoint
   * using the instance's project and model ids.
   * @private
   */
  _buildBaseURL() {
    return `/dataset`;
  }
}
